<template>
  <div class="overflow-hidden relative overflow-x-scroll">
    <!-- Loading overlay -->
    <div v-if="props.isLoading" class="max-h-full absolute inset-0 bg-white bg-opacity-90 z-10">
      <div class="flex items-center max-h-52 justify-center pt-52">
        <Loader2 class="w-4 h-4 mr-2 animate-spin" />
        <span class="text-sm text-gray-600 font-medium">Loading data...</span>
      </div>
    </div>

    <table class="min-w-full divide-y divide-gray-200 rounded-lg overflow-hidden table-fixed">
      <thead class="bg-white">
        <tr>
          <th scope="col" class="w-8 pl-2 py-3 text-center text-xs font-normal text-gray-500">#</th>
          <th scope="col" class="py-3 pr-2 text-xs font-normal text-gray-500 w-8 text-center">Section</th>
          <th scope="col" class="py-3 text-left text-xs font-normal text-gray-500 max-w-[48rem]">Title</th>
          <th scope="col" class="py-3 text-center text-xs font-normal text-gray-500 w-16">Points</th>
          <th scope="col" class="py-3 text-center text-xs font-normal text-gray-500 w-24">Details</th>
          <th scope="col" class="py-3 text-left text-xs font-normal text-gray-500 w-24">Status</th>
          <th scope="col" class="py-3 text-left text-xs font-normal text-gray-500 w-32">Action</th>
          <th scope="col" class="relative py-3 w-10"><span class="sr-only">Actions</span></th>
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200">
        <template v-for="(item, index) in localItems" :key="item.id">
          <tr @click="toggleExpand(item.id)" class="hover:bg-gray-50 cursor-pointer transition-all duration-200">
            <td class="py-4 w-8 pl-2 text-sm text-gray-400 text-center">{{ index + 1 }}</td>
            <td class="py-2 pr-2 whitespace-normal text-sm text-gray-400 text-center">
              <div class="flex justify-center items-center">
                <HoverCard v-if="item.path_step.task.section?.name && getSectionIcon(item.path_step.task.section?.name)">
                  <HoverCardTrigger>
                    <component
                      :is="getSectionIcon(item.path_step.task.section?.name)"
                      class="w-5 h-5 text-gray-500 cursor-pointer"
                    />
                  </HoverCardTrigger>
                  <HoverCardContent class="w-auto p-2 text-sm">
                    {{ item.path_step.task.section?.name }}
                  </HoverCardContent>
                </HoverCard>
              </div>
            </td>
            <td class="py-4 whitespace-normal text-sm text-gray-900 truncate">
              <a
                v-if="item.path_step.task.url && isUrl(item.path_step.task.url)"
                :href="item.path_step.task.url"
                target="_blank"
                class="text-gray-900 hover:text-cosmicPurple inline-flex items-center transition-all duration-200"
                @click.stop="handleOpenTask(item, $event)"
              >
                <span class="truncate">{{ item.path_step.task.name }}</span>
                <ExternalLink class="w-4 h-4 ml-1 shrink-0 text-cosmicPurple" />
                <span v-if="!item.path_step.is_required" class="text-xs bg-yellow-100 text-yellow-800 ml-2 px-2 py-0.5 rounded-full">Optional</span>
              </a>
              <template v-else>
                <span class="truncate">{{ item.path_step.task.name }}</span>
              </template>
            </td>
            
            <td class="py-4 whitespace-normal text-sm text-center">
              {{ item.path_step.points }}
            </td>
            
            <td class="py-4 whitespace-normal text-sm text-center">
              <FileText v-if="item.path_step.task.details" class="w-5 h-5 text-gray-500 mx-auto" />
            </td>
            
            <td class="py-4 whitespace-normal text-sm ">
              <span
                :class="getStatusClass(item.status)"
                class="px-2 py-1 text-xs rounded-full font-medium inline-flex justify-center w-[82px]"
              >
                {{ formatStatus(item.status) }}
              </span>
            </td>
            <td class="py-4 whitespace-normal text-sm">
              <button
                v-if="item.status === 'TO START'"
                @click.stop="updateTaskStatus(item.id, 'IN-PROGRESS')"
                class="bg-cosmicPurple text-white text-xs px-2 py-1 rounded-md min-w-36 hover:cursor-pointer hover:bg-cosmicPurple/90 hover:translate-y-[-1px] transition-all duration-200 flex items-center justify-center shadow"
                :disabled="updatingTask === item.id"
              >
                <Loader2 v-if="updatingTask === item.id" class="w-3 h-3 mr-1 inline animate-spin" />
                <Play v-else class="w-3 h-3 mr-1 inline" />
                Mark as started
              </button>
              <button
                v-else-if="item.status === 'IN-PROGRESS'"
                @click.stop="showReviewModal(item)"
                class="bg-lime-400 text-lime-900 text-xs px-2 py-1 rounded-md min-w-36 hover:cursor-pointer hover:translate-y-[-1px] transition-all duration-200 flex items-center justify-center shadow"
                :disabled="updatingTask === item.id"
              >
                <Loader2 v-if="updatingTask === item.id" class="w-3 h-3 mr-1 inline animate-spin" />
                <CircleCheckBig v-else class="w-3 h-3 mr-1 inline" />
                Mark as completed
              </button>
              <button
                v-else
                class="border-[1px] border-gray-300 text-gray-300 text-xs px-2 py-1 rounded-md min-w-36 flex items-center justify-center"
                disabled
              >
                <CircleCheckBig class="w-3 h-3 mr-1 inline" />
                Mark as completed
              </button>
            </td>
            <td class="py-4 text-sm text-gray-500">
              <div class="text-gray-400">
                <ChevronDown v-if="expandedItem !== item.id" class="w-5 h-5 mx-auto" />
                <ChevronUp v-else class="w-5 h-5 mx-auto" />
              </div>
            </td>
          </tr>
          <tr v-if="expandedItem === item.id" class="expanded-row">
            <td colspan="7" class="bg-lightGray p-2 expanded-cell">
              <TaskExpanded
                :item="item"
                :is-notes-loading="props.isNotesLoading"
                :is-updating="props.isUpdating"
                :personal-notes="localNotes[item.id] ?? null"
                :is-manage-view="false"
                :handle-open-task="handleOpenTask"
                @save-notes="(notes) => handleSaveNotes(item.id, notes)"
                @notes-interacted="() => emit('notes-interacted', item.id)"
              />
            </td>
          </tr>
        </template>
      </tbody>
    </table>

    <!-- Task Review Modal -->
    <TaskReview
      v-if="reviewingTask"
      :path-step-id="reviewingTask.path_step.id"
      :task-id="reviewingTask.path_step.task.id"
      :progress-id="reviewingTask.id"
      :path-step-name="reviewingTask.path_step.task.name"
      :on-complete="handleReviewComplete"
      :on-cancel="handleReviewCancel"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { ChevronDown, ChevronUp, Loader2, ExternalLink, CircleCheckBig, Play, FileText } from 'lucide-vue-next'
import type { PersonalNote, TableProgressItem, TaskStatus } from '@/types/curriculum'
import TaskExpanded from './TaskExpanded.vue'
import TaskReview from './TaskReview.vue'
import { isUrl } from '@/utils/isUrl'
import { getSectionIcon } from '@/const/curriculumSections'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '@/components/ui/hover-card'
import { useToast } from 'primevue/usetoast'
import { sortCurriculumItems } from '@/utils/curriculumSorting'

// Define Props using the flexible type
const props = defineProps<{
  items: TableProgressItem[]
  isLoading: boolean
  isNotesLoading: boolean
  isUpdating: boolean
  personalNotes: Record<number, PersonalNote | null>
}>()

// Define Emits
const emit = defineEmits<{
  (e: 'toggle-expand', id: number): void
  (e: 'save-notes', payload: { id: number; notes: string }): void
  (e: 'notes-interacted', id: number): void
  (e: 'update-status', payload: { id: number; status: TaskStatus }): void
}>()

const toast = useToast()
const updatingTask = ref<number | null>(null)
const reviewingTask = ref<TableProgressItem | null>(null)
const expandedItem = ref<number | null>(null)

// Local state for items and notes
const localItems = ref<TableProgressItem[]>(props.items)
const localNotes = ref<Record<number, PersonalNote | null>>(props.personalNotes || {})

// Watch for prop changes
watch(() => props.items, (newItems) => {
  localItems.value = newItems
}, { deep: true })

watch(() => props.personalNotes, (newNotes) => {
  localNotes.value = newNotes || {}
}, { deep: true })

const sortedItems = computed<TableProgressItem[]>(() => {
  return sortCurriculumItems(localItems.value || [])
})

const toggleExpand = async (id: number) => {
  expandedItem.value = expandedItem.value === id ? null : id
  if (expandedItem.value === id) {
    emit('toggle-expand', id)
  }
}

const handleOpenTask = async (item: TableProgressItem, event?: Event) => {
  if (event) {
    event.preventDefault()
    event.stopPropagation()
  }
  
  const taskUrl = item.path_step.task.url
  if (!taskUrl || !isUrl(taskUrl)) return
  
  if (item.status === 'TO START') {
    updatingTask.value = item.id
    try {
      await updateTaskStatus(item.id, 'IN-PROGRESS')
      window.open(taskUrl, '_blank')
    } catch (error) {
      window.open(taskUrl, '_blank')
    } finally {
      updatingTask.value = null
    }
  } else {
    window.open(taskUrl, '_blank')
  }
}

const updateTaskStatus = async (id: number, status: TaskStatus) => {
  updatingTask.value = id
  
  try {
    // Update local state
    const itemIndex = localItems.value.findIndex(item => item.id === id)
    if (itemIndex !== -1) {
      localItems.value[itemIndex].status = status
    }
    
    // Emit the update event
    emit('update-status', { id, status })
    
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Progress status updated successfully',
      life: 3000
    })
  } catch (error) {
    console.error('Error updating status:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update progress status',
      life: 5000
    })
  } finally {
    updatingTask.value = null
  }
}

const handleSaveNotes = async (id: number, notes: string) => {
  try {
    // Update local notes
    localNotes.value[id] = {
      id: Date.now(), // Generate a temporary ID
      notes,
      progress: id
    }
    
    emit('save-notes', { id, notes })
    
    toast.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Note saved successfully',
      life: 3000
    })
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save note',
      life: 5000
    })
  }
}

const getStatusClass = (status: string) => {
  switch (status) {
    case 'DONE':
      return 'bg-lime-200 text-lime-800'
    case 'IN-PROGRESS':
      return 'bg-cosmicPurple/20 text-cosmicPurple'
    case 'TO START':
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const formatStatus = (status: string) => {
  switch (status) {
    case 'TO START':
      return 'Incomplete'
    case 'IN-PROGRESS':
      return 'In Progress'
    case 'DONE':
      return 'Completed'
    default:
      return status
  }
}

const showReviewModal = (item: TableProgressItem) => {
  reviewingTask.value = item
}

const handleReviewComplete = () => {
  if (reviewingTask.value) {
    // Update local state
    const itemIndex = localItems.value.findIndex(item => item.id === reviewingTask.value?.id)
    if (itemIndex !== -1) {
      localItems.value[itemIndex].status = 'DONE'
    }
    
    // Emit update event
    emit('update-status', { id: reviewingTask.value.id, status: 'DONE' })
  }
  reviewingTask.value = null
}

const handleReviewCancel = () => {
  reviewingTask.value = null
}
</script>

<style scoped>
.expanded-row {
  transition: all 0.3s ease-in-out;
}

.expanded-cell {
  animation: expand 0.3s ease-in-out forwards;
  overflow: hidden;
}

@keyframes expand {
  from {
    max-height: 0;
    padding-top: 0;
    padding-bottom: 0;
  }
  to {
    max-height: 1000px;
    padding: 0.5rem;
  }
}
</style> 